import type { Path, UseFormReturn } from 'react-hook-form';
import { Disclosure, DisclosureButton, Labeled, Switch } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IGeneralSettingsFormValues } from './GeneralSettingsForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';

export type IGeneralSettingsFormAdminArticlesSectionProps = IContainerProps & {
  form: UseFormReturn<IGeneralSettingsFormValues>;
  submittingFields?: Array<Path<IGeneralSettingsFormValues>>;
  onChange?: (
    form: UseFormReturn<IGeneralSettingsFormValues>,
    updatedFields?: Array<Path<IGeneralSettingsFormValues>>,
  ) => void;
};

export const GeneralSettingsFormAdminArticlesSection: React.FC<
  IGeneralSettingsFormAdminArticlesSectionProps
> = (props) => {
  const { sx, form, submittingFields, onChange } = props;
  const { t } = useTranslation();

  const usersEnabled = form.watch('usersEnabled');

  return (
    <Disclosure
      sx={sx}
      initiallyExpanded={
        form.watch('feedArticleCommentsEnabled') ||
        form.watch('feedArticleShareEnabled')
      }
      trigger={
        <DisclosureButton>
          {t('GeneralSettingsFormAdminArticlesSection.title')}
        </DisclosureButton>
      }
    >
      <FormSection sub>
        <Labeled
          label={t(
            'GeneralSettingsFormAdminArticlesSection.field.feedArticleCommentsEnabled.label',
          )}
          supportingText={t(
            'GeneralSettingsFormAdminArticlesSection.field.feedArticleCommentsEnabled.supportingText',
          )}
          hasError={!usersEnabled}
          errorText={
            !usersEnabled
              ? t('GeneralSettingsFormAdminArticlesSection.error.usersRequired')
              : undefined
          }
          labelPosition="right"
        >
          <FormField
            name="feedArticleCommentsEnabled"
            control={form.control}
            as={Switch}
            rules={{
              onChange: () => onChange?.(form, ['feedArticleCommentsEnabled']),
            }}
            loading={submittingFields?.includes('feedArticleCommentsEnabled')}
          />
        </Labeled>
        <Labeled
          label={t(
            'GeneralSettingsFormAdminArticlesSection.field.feedArticleShareEnabled.label',
          )}
          labelPosition="right"
        >
          <FormField
            name="feedArticleShareEnabled"
            control={form.control}
            as={Switch}
            rules={{
              onChange: () => onChange?.(form, ['feedArticleShareEnabled']),
            }}
            loading={submittingFields?.includes('feedArticleShareEnabled')}
          />
        </Labeled>
      </FormSection>
    </Disclosure>
  );
};
